<template>
  <table-wrapper>
    <v-data-table-server
      class="coronet-table--highlightable"
      :loading="loading"
      :headers="headers"
      :items="items"
      :fixed-header="true"
      :show-select="false"
      item-key="id"
      :items-length="totalItems"
      sort-asc-icon="$sortArrow"
      sort-desc-icon="$sortArrow"
      :page="pagination.page + 1"
      :items-per-page="pagination.pageSize"
      @update:options="handleOptionsUpdate"
    >
      <template #item="{ item, index }">
        <tr :key="item.id" :data-testid="`table-row-${index}`">
          <td class="pa-4" :data-testid="`device-allow-list-table-value-col-${index}`">
            <div class="d-flex align-center value-col">
              <v-icon class="mr-2" size="35" color="primary" :icon="getIcon(item.type)" />
              <div>
                <ellipsified-copy-text
                  class="body2 font-weight-bold"
                  width="550px"
                  :text="item.value"
                />
                <span
                  v-if="item.timestamp"
                  class="caption text-indigo-medium"
                  :data-testid="'device-allow-list-table-date'"
                >
                  {{ getFormattedDateTime(item.timestamp) }}
                </span>
              </div>
            </div>
          </td>
          <td :data-testid="`device-allow-list-table-status-col-${index}`">
            <span v-if="!item.blocked" class="text-green-dark">{{
              $t(`phishingSettings.allowed`)
            }}</span>
            <span v-else class="text-red-dark">{{ $t(`phishingSettings.blocked`) }}</span>
          </td>
          <td :data-testid="`device-allow-list-table-description-col-${index}`">
            <ellipsified-copy-text v-if="item.description" width="370px" :text="item.description" />
          </td>
          <td class="coro-table-col--sticky-right">
            <v-menu bottom left>
              <template v-slot:activator="{ props }">
                <v-icon
                  :class="{ 'icon-btn-disabled': item.inheritedFromParent }"
                  icon="$dots"
                  v-bind="props"
                ></v-icon>
              </template>
              <v-list>
                <v-list-item
                  v-for="(action, index) in actions"
                  :key="index"
                  :value="index"
                  :data-testid="`users-table-action-btn-row-${index}`"
                  @click="
                    onActionClick({
                      action: action.actionKey,
                      callback: action.callback,
                      item,
                    })
                  "
                >
                  <v-list-item-title
                    :data-testid="`device-allow-list-table-${action.actionKey}-action`"
                  >
                    {{ $t(`devicesSettings.allowBlockList.actions.${action.actionKey}`) }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import TableWrapper from "@/components/TableWrapper.vue";
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { getFormattedDateTime } from "@/_helpers/date";
import { useDialogsStore } from "@/_store/dialogs.module";
import { useI18n } from "vue-i18n";
import type { DeviceAllowBlockListResponseItem } from "@/_store/endpoint-security/device-allow-block-list.module";
import type { GenericCallback, Pagination } from "@/types";
import EllipsifiedCopyText from "@/components/EllipsifiedCopyText.vue";
import { confirmationDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";

export default defineComponent({
  components: {
    EllipsifiedCopyText,
    TableWrapper,
  },
  props: {
    items: {
      type: Array as PropType<DeviceAllowBlockListResponseItem[]>,
      required: true,
    },
    actions: {
      type: Object as PropType<Array<{ actionKey: string; callback: GenericCallback }>>,
      required: true,
    },
    context: {
      type: String,
      default: "",
    },
    pagination: {
      type: Object as PropType<Pagination>,
      default: () => {
        return {
          page: 0,
          pageSize: 25,
        };
      },
    },
    totalItems: {
      type: Number,
      required: true,
    },
    disableActions: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
    },
  },
  emits: ["page-changed"],
  setup(props, { emit }) {
    const dialogsStore = useDialogsStore();
    const i18n = useI18n();
    const iconToTypeMap: Record<string, string> = {
      file: "$fileEmpty",
      process: "$process",
      folder: "$folder",
    };

    const headers = [
      {
        title: i18n.t("devicesSettings.allowBlockList.table.value"),
        key: "value",
        sortable: false,
      },
      {
        title: i18n.t("devicesSettings.allowBlockList.table.list"),
        key: "list",
        sortable: false,
      },
      {
        title: i18n.t("devicesSettings.allowBlockList.table.description"),
        key: "description",
        sortable: false,
      },
      {
        title: "",
        headerProps: {
          "aria-label": i18n.t("ariaLabels.actionsColumn"),
        },
      },
    ];

    const getIcon = (type: string) => {
      // get rid of "edr" prefix returned by BE for EDR Allow/Block page
      const normalizedType = type.replace("edr", "").toLowerCase();
      return iconToTypeMap[`${normalizedType}`];
    };

    const onActionClick = (event: {
      action: string;
      callback: GenericCallback;
      item: DeviceAllowBlockListResponseItem;
    }) => {
      const dialogConfig = confirmationDialogsConfigConstructor({
        item: event.item,
        action: event.action,
        callback: event.callback,
        text: event.item.blocked
          ? i18n.t(`modals.${event.action}.descriptionBlocked`)
          : i18n.t(`modals.${event.action}.descriptionAllowed`),
        disable: props.disableActions,
      });

      dialogsStore.openDialog(dialogConfig);
    };

    /**
     * This new handler function checks if the pagination has actually changed
     * before emitting the 'page-changed' event.
     */
    const handleOptionsUpdate = (event: { page: number; itemsPerPage: number }) => {
      const newPage = event.page - 1; // v-data-table is 1-based, we want 0-based
      const newPageSize = event.itemsPerPage;

      // Only emit if the page or pageSize is different from the current props
      if (newPage !== props.pagination.page || newPageSize !== props.pagination.pageSize) {
        emit("page-changed", {
          pageSize: newPageSize,
          page: newPage,
        });
      }
    };

    return {
      getIcon,
      onActionClick,
      getFormattedDateTime,
      handleOptionsUpdate,
      headers,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(*) {
  .v-table__wrapper {
    max-height: calc(100vh - 380px) !important;
  }
}
</style>
